<script setup lang="ts">
import { computed, ref } from "vue";
import { Menu } from "@ark-ui/vue";
import { UiButton, UiIconButton, useToaster } from "@shopexperts/shared";
import type { UseMutationReturnType } from "@tanstack/vue-query";
import type { ProjectDetail, ProjectSide, ProjectStatus, RawQuoteRecord } from "@/v2Projects/types";
import { isClientQuoteActionable } from "@/v2Projects/utils/quotePresentation";
import ProjectStatusConfirmDialog, {
  type ProjectStatusConfirmPayload,
  type ProjectStatusDialogAction,
} from "@/v2Projects/components/ProjectStatusConfirmDialog.vue";
import { reportProjectUserError } from "@/v2Projects/userErrors";
import type { ProjectStatusUpdate } from "@/v2Projects/composables/useProjectStatus";

const TERMINAL_STATUSES: ProjectStatus[] = ["completed", "cancelled", "rejected"];

type UpdateProjectStatusMutation = UseMutationReturnType<unknown, Error, ProjectStatusUpdate, unknown>;

type ActionButtonConfig =
  | {
      kind: "create_quote";
      label: string;
      variant: "outline";
    }
  | ({
      kind: "status_change";
      variant: "primary" | "outline" | "danger";
    } & ProjectStatusDialogAction);

interface StatusChangeAction extends ProjectStatusDialogAction {
  variant: "primary" | "outline" | "danger";
}

const props = defineProps<{
  project: ProjectDetail;
  side: ProjectSide;
  quotes: RawQuoteRecord[];
  allowedTransitions: ProjectStatus[];
  updateProjectStatus: UpdateProjectStatusMutation;
}>();

const emit = defineEmits<{
  createQuote: [];
}>();

const toaster = useToaster();
const isDialogOpen = ref(false);
const activeAction = ref<StatusChangeAction | null>(null);

const hasActionableQuote = computed(() =>
  props.quotes.some((quote) => isClientQuoteActionable(quote)),
);

const isTerminalProject = computed(() =>
  TERMINAL_STATUSES.includes(props.project.project_status),
);

const expertActions = computed<ActionButtonConfig[]>(() => {
  if (props.side !== "expert" || isTerminalProject.value) return [];

  const actions: ActionButtonConfig[] = [];
  const canCreateQuote =
    props.project.project_status === "quoted" ||
    props.project.project_status === "in_progress" ||
    (props.project.project_status === "pending" && !hasActionableQuote.value);

  if (canCreateQuote) {
    actions.push({
      kind: "create_quote",
      label: "Send Quote",
      variant: "outline",
    });
  }

  if (props.allowedTransitions.includes("submitted")) {
    actions.push({
      kind: "status_change",
      targetStatus: "submitted",
      label: "Submit Work",
      description: "Mark this project as delivered so the client can review and complete it.",
      confirmLabel: "Submit Work",
      offersAutoReviewRequests: true,
      variant: "primary",
    });
  }

  if (props.allowedTransitions.includes("rejected")) {
    actions.push({
      kind: "status_change",
      targetStatus: "rejected",
      label: "Reject Project",
      description: "Rejecting the project closes it from the expert side and removes it from the active workflow.",
      confirmLabel: "Reject Project",
      requiresReason: true,
      variant: "danger",
    });
  }

  return actions;
});

const clientActions = computed<ActionButtonConfig[]>(() => {
  if (props.side !== "client" || isTerminalProject.value) return [];

  const actions: ActionButtonConfig[] = [];

  if (props.allowedTransitions.includes("completed")) {
    actions.push({
      kind: "status_change",
      targetStatus: "completed",
      label: "Complete Project",
      description: "Confirm delivery is approved and close the project as completed.",
      confirmLabel: "Complete Project",
      variant: "primary",
    });
  }

  if (props.allowedTransitions.includes("cancelled")) {
    actions.push({
      kind: "status_change",
      targetStatus: "cancelled",
      label: "Cancel Project",
      description: "Cancel this project before work progresses further. This action removes it from the active workflow.",
      confirmLabel: "Cancel Project",
      variant: "danger",
    });
  }

  return actions;
});

const visibleActions = computed(() =>
  props.side === "expert" ? expertActions.value : clientActions.value,
);

function openConfirmation(action: ActionButtonConfig) {
  if (action.kind === "create_quote") {
    emit("createQuote");
    return;
  }
  activeAction.value = action;
  isDialogOpen.value = true;
}

function handleMenuSelect(actionLabel: string) {
  const action = visibleActions.value.find((entry) => entry.label === actionLabel);
  if (!action) return;
  openConfirmation(action);
}

function actionTone(action: ActionButtonConfig): "primary" | "danger" | "default" {
  if (action.kind === "create_quote") return "default";
  if (action.variant === "danger") return "danger";
  if (action.variant === "primary") return "primary";
  return "default";
}

async function confirmAction({ reason, autoReviewRequestsEnabled }: ProjectStatusConfirmPayload) {
  const action = activeAction.value;
  if (!action) return;

  try {
    await props.updateProjectStatus.mutateAsync({
      status: action.targetStatus,
      rejectionReason: action.targetStatus === "rejected" ? reason : undefined,
      autoReviewRequestsEnabled,
    });
    toaster.create({ title: `${action.label} successful`, type: "success" });
    isDialogOpen.value = false;
    activeAction.value = null;
  } catch (error) {
    const message = reportProjectUserError("updateProjectStatus", error, {
      projectId: props.project.id,
      side: props.side,
      extra: { targetStatus: action.targetStatus },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}
</script>

<template>
  <div v-if="visibleActions.length > 0" class="flex flex-wrap items-center justify-end gap-2">
    <UiButton
      v-for="action in visibleActions"
      :key="`${side}-${project.project_status}-${action.label}`"
      :variant="action.variant"
      :label="action.label"
      class="max-sm:hidden"
      @click="openConfirmation(action)"
    />

    <!-- `max-sm:contents` wrapper handles desktop visibility (UiIconButton bakes `inline-flex` and overrides our `hidden`). -->
    <div class="hidden max-sm:contents">
      <Menu.Root :positioning="{ placement: 'bottom-end', gutter: 8 }" @select="(detail) => handleMenuSelect(String(detail.value))">
        <Menu.Trigger as-child>
          <UiIconButton
            icon="dots-vertical"
            variant="ghost"
            aria-label="Project actions"
          />
        </Menu.Trigger>
        <Teleport to="body" defer>
          <Menu.Positioner class="z-2000">
            <Menu.Content
              class="min-w-[190px] overflow-hidden rounded-md border border-se-gray-300 bg-white shadow-md py-1 animate-in fade-in zoom-in-95 duration-100"
            >
              <Menu.Item
                v-for="action in visibleActions"
                :key="`menu-${side}-${project.project_status}-${action.label}`"
                :value="action.label"
                class="flex w-full cursor-pointer items-center justify-between px-4 py-2 text-h5 transition-colors data-highlighted:bg-se-gray-100"
                :class="{
                  'text-se-danger-dark': actionTone(action) === 'danger',
                  'text-se-dark': actionTone(action) !== 'danger',
                }"
              >
                <span>{{ action.label }}</span>
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Teleport>
      </Menu.Root>
    </div>

    <ProjectStatusConfirmDialog
      v-model="isDialogOpen"
      :action="activeAction"
      :current-status="project.project_status"
      :is-loading="updateProjectStatus.isPending.value"
      :auto-review-requests-enabled="project.auto_review_requests_enabled"
      @confirm="confirmAction"
    />
  </div>
</template>
